import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Field, Panel } from '../components/ui.js';
import { num } from '../lib/format.js';

/**
 * Přednastavené segmenty (D-306) a povinné výluky rozesílek. Šablona vytvoří segment jedním klikem;
 * před uložením ukáže počet v hubu, počet adres v zrcadle SmartEmailingu a maskovanou ukázku.
 */

interface Template {
  id: string;
  name: string;
  description: string;
  params: Array<{ key: string; required: boolean; hint: string }>;
}
interface Mirror {
  members: number;
  afterExclusions: number;
  sample: string[];
}
interface Result {
  saved: boolean;
  code?: string;
  name: string;
  members: number;
  sample: Array<{ email: string }>;
  mirror: Mirror | null;
  popis: string;
}
interface Exclusions {
  fixed: Array<{ code: string; popis: string }>;
  listIds: number[];
  lists: Array<{ id: number; name: string }>;
}

const NUMERIC = new Set(['days', 'months']);
const parseIds = (text: string): number[] =>
  text
    .split(/[ ,;]+/)
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);

export function SegmentTemplates({ canEdit }: { canEdit: boolean }): JSX.Element {
  const client = useQueryClient();
  const templates = useQuery({
    queryKey: ['segment-templates'],
    queryFn: () => api.get<{ items: Template[] }>('/api/segments/templates'),
  });
  const exclusions = useQuery({
    queryKey: ['broadcast-exclusions'],
    queryFn: () => api.get<Exclusions>('/api/broadcast-exclusions'),
  });
  const [templateId, setTemplateId] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<Result | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [exclusionText, setExclusionText] = useState<string | null>(null);

  const template = templates.data?.items.find((t) => t.id === templateId);
  const params = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [key, raw] of Object.entries(values)) {
      const v = raw.trim();
      if (v === '') continue;
      if (NUMERIC.has(key)) out[key] = Number(v);
      else if (key === 'seListIds' || key === 'notSeListIds') out[key] = parseIds(v);
      else out[key] = v;
    }
    return out;
  };

  const run = useMutation({
    mutationFn: (save: boolean) =>
      api.post<Result>(save ? '/api/segments/from-template/save' : '/api/segments/from-template', {
        templateId,
        params: params(),
      }),
    onSuccess: (data) => {
      setResult(data);
      setNote(null);
      if (data.saved) void client.invalidateQueries({ queryKey: ['segments'] });
    },
    onError: (err: Error) => {
      setResult(null);
      setNote(err.message);
    },
  });

  const saveExclusions = useMutation({
    mutationFn: (listIds: number[]) => api.put('/api/broadcast-exclusions', { listIds }),
    onSuccess: () => {
      setExclusionText(null);
      void client.invalidateQueries({ queryKey: ['broadcast-exclusions'] });
    },
    onError: (err: Error) => setNote(err.message),
  });

  return (
    <>
      <Panel title="Povinné výluky rozesílek">
        <p className="note">
          Tyto výluky platí u každé rozesílky a nejdou z ní odebrat. Hub je vynucuje při náhledu i
          při odeslání a každý vyřazený příjemce má v náhledu uvedený důvod.
        </p>
        <ul>
          {exclusions.data?.fixed.map((f) => (
            <li key={f.code}>
              {f.popis} <code>{f.code}</code>
            </li>
          ))}
          <li>
            Členové vylučovacích seznamů SmartEmailingu (X_bot, vyloučit, bounce ...),{' '}
            <code>excluded_list</code>:{' '}
            {exclusions.data?.lists.length
              ? exclusions.data.lists.map((l) => `${l.name} (${l.id})`).join(', ')
              : 'zatím žádné seznamy nejsou zadané'}
          </li>
        </ul>
        {canEdit ? (
          <div className="row">
            <Field label="ID vylučovacích seznamů SE (oddělte čárkou)">
              <input
                value={exclusionText ?? (exclusions.data?.listIds ?? []).join(', ')}
                onChange={(e) => setExclusionText(e.target.value)}
              />
            </Field>
            <button
              className="btn"
              disabled={exclusionText === null}
              onClick={() => saveExclusions.mutate(parseIds(exclusionText ?? ''))}
            >
              Uložit výluky
            </button>
          </div>
        ) : null}
      </Panel>

      <Panel title="Přednastavené segmenty">
        <p className="note">
          Vyberte šablonu, doplňte parametry a nechte spočítat náhled. Segment se vyhodnocuje při
          každém náhledu a odeslání rozesílky, nekopíruje se do seznamu.
        </p>
        <div className="row">
          <Field label="Šablona">
            <select
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setValues({});
                setResult(null);
              }}
            >
              <option value="">— vyberte —</option>
              {templates.data?.items.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          {template?.params.map((p) => (
            <Field key={p.key} label={`${p.key}${p.required ? ' *' : ''}`} hint={p.hint}>
              <input
                value={values[p.key] ?? ''}
                onChange={(e) => setValues({ ...values, [p.key]: e.target.value })}
              />
            </Field>
          ))}
        </div>
        {template ? <p className="note">{template.description}</p> : null}
        <div className="row">
          <button className="btn" disabled={!template} onClick={() => run.mutate(false)}>
            Spočítat náhled
          </button>
          {canEdit ? (
            <button className="btn pri" disabled={!template} onClick={() => run.mutate(true)}>
              Vytvořit segment
            </button>
          ) : null}
        </div>
        {note ? (
          <p className="hint" role="status">
            {note}
          </p>
        ) : null}
        {result ? (
          <div role="status">
            <p>
              <strong>{result.name}</strong>
              {result.saved ? ` — uloženo jako ${result.code}` : ' — náhled'}: {result.popis}
            </p>
            <p>
              V hubu {num(result.members)} kontaktů
              {result.mirror
                ? `; v zrcadle SmartEmailingu ${num(result.mirror.members)} adres, po povinných výlukách ${num(result.mirror.afterExclusions)}`
                : '; zrcadlo SmartEmailingu tuto definici nezvládne'}
              .
            </p>
            {result.mirror?.sample.length ? (
              <p className="note">Ukázka: {result.mirror.sample.join(', ')}</p>
            ) : result.sample.length ? (
              <p className="note">Ukázka: {result.sample.map((s) => s.email).join(', ')}</p>
            ) : null}
          </div>
        ) : null}
      </Panel>
    </>
  );
}
